
import React from "react";
import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { WaitlistFormValues } from "../../schema";
import { useNursingHomesByCounty } from "@/hooks/use-nursing-homes-by-county";
import { useCounties } from "@/hooks/use-counties";

interface NursingHomeSelectorProps {
  control: Control<WaitlistFormValues>;
  selectedCountySlugs: string[];
  selectedNursingHomes: string[];
  setSelectedNursingHomes: React.Dispatch<React.SetStateAction<string[]>>;
  openToSuggestions: boolean;
  setOpenToSuggestions: (open: boolean) => void;
}

export function NursingHomeSelector({
  control,
  selectedCountySlugs,
  selectedNursingHomes,
  setSelectedNursingHomes,
  openToSuggestions,
  setOpenToSuggestions,
}: NursingHomeSelectorProps) {
  const { data: counties } = useCounties();
  const { data: nursingHomes, isLoading: nursingHomesLoading } = useNursingHomesByCounty(selectedCountySlugs);

  const handleNursingHomeToggle = (nhSlug: string) => {
    setSelectedNursingHomes(prev => 
      prev.includes(nhSlug)
        ? prev.filter(slug => slug !== nhSlug)
        : [...prev, nhSlug]
    );
  };

  return (
    <FormField
      control={control}
      name="specificNursingHome"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Specific Nursing Home(s)</FormLabel>
          <div className="border rounded-md p-4 space-y-2 max-h-60 overflow-y-auto">
            {selectedCountySlugs.length > 0 && nursingHomesLoading && (
              <div className="text-sm text-muted-foreground">Loading nursing homes...</div>
            )}
            
            {selectedCountySlugs.length > 0 && !nursingHomesLoading && nursingHomes?.length === 0 && (
              <div className="text-sm text-muted-foreground">No nursing homes found for the selected counties</div>
            )}
            
            {selectedCountySlugs.length > 0 && !nursingHomesLoading && nursingHomes?.length > 0 && (
              <div className="space-y-2">
                {nursingHomes.map((home) => (
                  <div key={home.nh_slug} className="flex items-center space-x-2">
                    <Checkbox 
                      id={home.nh_slug}
                      checked={selectedNursingHomes.includes(home.nh_slug)}
                      onCheckedChange={() => handleNursingHomeToggle(home.nh_slug)}
                      disabled={openToSuggestions}
                    />
                    <label
                      htmlFor={home.nh_slug}
                      className={`text-sm cursor-pointer ${openToSuggestions ? 'text-gray-400' : ''}`}
                    >
                      {home.center_name} ({counties?.find(c => c.slug === home.county_slug)?.name})
                    </label>
                  </div>
                ))}
              </div>
            )}
            
            {selectedCountySlugs.length === 0 && (
              <div className="text-sm text-muted-foreground">
                Select counties to view nursing homes
              </div>
            )}
          </div>
          
          <div className="mt-2 flex items-center space-x-2">
            <Checkbox 
              id="open-to-suggestions"
              checked={openToSuggestions}
              onCheckedChange={(checked) => {
                setOpenToSuggestions(!!checked);
                if (checked) {
                  setSelectedNursingHomes([]);
                }
              }}
            />
            <label
              htmlFor="open-to-suggestions"
              className="text-sm font-medium cursor-pointer"
            >
              Open to suggestions
            </label>
          </div>
          
          <FormControl>
            <Input 
              type="hidden" 
              {...field} 
              value={
                openToSuggestions 
                  ? "open_to_suggestions" 
                  : selectedNursingHomes.join(",")
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
